import { db as defaultFirestore, firebase_rdb as defaultRealtimeRoot } from '../Config/Config';

export const MAXPARK_CLIENT_ID = 'maxpark-220731';
export const MAXPARK_ADMIN_CLAIM = 'maxparkAdmin';

export const hasMaxParkAdminClaim = (claims = {}) => claims[MAXPARK_ADMIN_CLAIM] === true;

const isSafeFirebaseUid = (uid) => (
  typeof uid === 'string'
  && uid.length > 0
  && uid.length <= 128
  && !/[.#$\[\]\/]/.test(uid)
);

const adminAccessPath = (uid) => (
  `clients/${MAXPARK_CLIENT_ID}/adminAccess/${uid}`
);

const readRealtimeAdminAccess = async (uid, realtimeRoot = defaultRealtimeRoot) => {
  if (!realtimeRoot || typeof realtimeRoot.child !== 'function') {
    throw new Error('Realtime admin access projection is unavailable');
  }

  const snapshot = await realtimeRoot.child(adminAccessPath(uid)).once('value');
  return snapshot?.val?.() ?? null;
};

const readFirestoreAdminAccess = async (uid, firestore = defaultFirestore) => {
  if (!firestore || typeof firestore.doc !== 'function') {
    throw new Error('Firestore admin access projection is unavailable');
  }

  const snapshot = await firestore.doc(adminAccessPath(uid)).get();
  return snapshot?.exists ? snapshot.data() : null;
};

const classifyAdminAccessProjection = (projection, uid) => {
  if (
    !projection
    || typeof projection !== 'object'
    || Array.isArray(projection)
    || projection.clientId !== MAXPARK_CLIENT_ID
    || projection.uid !== uid
    || typeof projection.enabled !== 'boolean'
  ) {
    return 'invalid';
  }

  return projection.enabled ? 'enabled' : 'disabled';
};

export const resolveMaxParkAdminAccess = async (user, options = {}) => {
  if (!user) {
    return {
      allowed: false,
      source: 'signed-out'
    };
  }

  const getTokenResult = options.getTokenResult || user.getIdTokenResult?.bind(user);
  const forceRefresh = options.forceRefresh !== undefined ? options.forceRefresh : true;

  if (typeof getTokenResult === 'function') {
    let tokenResult;

    try {
      tokenResult = await getTokenResult(forceRefresh);
    } catch {
      return {
        allowed: false,
        source: 'admin-access-unavailable'
      };
    }

    if (hasMaxParkAdminClaim(tokenResult?.claims || {})) {
      if (!isSafeFirebaseUid(user.uid)) {
        return {
          allowed: false,
          source: 'admin-access-reconciliation-required'
        };
      }

      // TEMP: Keep claims authoritative until the adminAccess migration has a zero-blocker dry run.
      if (options.requireCurrentProjection !== true) {
        return {
          allowed: true,
          source: 'custom-claim'
        };
      }

      const readRealtimeProjection = options.readRealtimeProjection
        || ((uid) => readRealtimeAdminAccess(uid, options.realtimeRoot));
      const readFirestoreProjection = options.readFirestoreProjection
        || ((uid) => readFirestoreAdminAccess(uid, options.firestore));

      let realtimeProjection;
      let firestoreProjection;

      try {
        [realtimeProjection, firestoreProjection] = await Promise.all([
          readRealtimeProjection(user.uid),
          readFirestoreProjection(user.uid)
        ]);
      } catch {
        return {
          allowed: false,
          source: 'admin-access-unavailable'
        };
      }

      const realtimeState = classifyAdminAccessProjection(realtimeProjection, user.uid);
      const firestoreState = classifyAdminAccessProjection(firestoreProjection, user.uid);

      if (realtimeState === 'invalid' || firestoreState === 'invalid' || realtimeState !== firestoreState) {
        return {
          allowed: false,
          source: 'admin-access-reconciliation-required'
        };
      }

      if (realtimeState === 'disabled') {
        return {
          allowed: false,
          source: 'admin-access-disabled'
        };
      }

      return {
        allowed: true,
        source: 'custom-claim-and-current-access'
      };
    }
  }

  return {
    allowed: false,
    source: 'missing-admin-role'
  };
};
